import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  RefreshCw,
  Plus,
  Facebook,
  CheckCircle,
  AlertCircle,
  Trash2,
  ExternalLink,
  Users,
  Building2,
  Settings,
  Info,
  Shield
} from 'lucide-react';
import styles from './ConnectMetaAds.module.css';

const ConnectMetaAds = () => {
  const [accounts, setAccounts] = useState({ pages: [], adAccounts: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [disconnecting, setDisconnecting] = useState(null);
  const userId = localStorage.getItem('userId');
  const API = 'https://prod.toxboxai.com/api/meta';

  const navigate = useNavigate();

  useEffect(() => {
    if (userId) fetchAccounts();

    // Handle OAuth callback parameters
    const params = new URLSearchParams(window.location.search);
    if (params.get('error')) {
      setError(`Connection failed: ${decodeURIComponent(params.get('error'))}`);
    }
    if (params.get('success')) {
      setSuccess(`Successfully connected! ${params.get('pages') || 0} pages and ${params.get('ads') || 0} ad accounts linked.`);
    }

    // Clean up URL
    window.history.replaceState({}, '', window.location.pathname);
  }, [userId]);

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/${userId}/accounts`);
      if (res.data.success) {
        setAccounts(res.data);
        setError('');
      }
    } catch (e) {
      setError('Failed to load connected accounts');
      console.error('Fetch accounts error:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = () => {
    setError('');
    setSuccess('');

    const scopes = [
      "pages_show_list",
      "pages_read_engagement",
      "leads_retrieval",
      "ads_management",
      "ads_read",
      "pages_manage_metadata",
      "business_management",
      "pages_manage_ads"
    ].join(",");

    const redirectUri = encodeURIComponent("https://prod.toxboxai.com/api/meta/callback");
    const clientId = "1707903949881063";
    const authUrl = `https://www.facebook.com/v21.0/dialog/oauth?` +
      `client_id=${clientId}` +
      `&redirect_uri=${redirectUri}` +
      `&state=${userId}` +
      `&response_type=code` +
      `&scope=${scopes}`;

    window.location.href = authUrl;
  };

  const handleDisconnectAd = async (adAccountId, adAccountName) => {
    if (!window.confirm(`Are you sure you want to disconnect "${adAccountName}"? This will stop campaign data sync.`)) {
      return;
    }

    setDisconnecting(adAccountId);
    try {
      await axios.delete(`${API}/${userId}/adaccount/${adAccountId}/disconnect`);
      await fetchAccounts();
      setSuccess(`Successfully disconnected ad account "${adAccountName}"`);
    } catch (e) {
      setError('Failed to disconnect ad account');
      console.error('Disconnect error:', e);
    } finally {
      setDisconnecting(null);
    }
  };

  const handleDisconnectPage = async (pageId, pageName) => {
    if (!window.confirm(`Are you sure you want to disconnect "${pageName}"? This will stop lead collection.`)) {
      return;
    }

    setDisconnecting(pageId);
    try {
      await axios.delete(`${API}/${userId}/${pageId}/disconnect`);
      await fetchAccounts();
      setSuccess(`Successfully disconnected page "${pageName}"`);
    } catch (e) {
      setError('Failed to disconnect page');
      console.error('Disconnect error:', e);
    } finally {
      setDisconnecting(null);
    }
  };

  if (!userId) {
    return (
      <div className={styles.container}>
        <div className={styles.loginPrompt}>
          <Shield size={48} />
          <h2>Authentication Required</h2>
          <p>Please log in to manage your Meta advertising connections.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>
            <Facebook size={28} />
            Meta Integration
          </h1>
          <p className={styles.subtitle}>
            Connect your Facebook pages and ad accounts to sync campaigns and leads
          </p>
        </div>

        <div className={styles.headerActions}>
          <button
            onClick={fetchAccounts}
            className={styles.refreshBtn}
            disabled={loading}
          >
            <RefreshCw size={16} className={loading ? styles.spinning : ''} />
            Refresh
          </button>

          <button
            onClick={handleConnect}
            className={styles.connectBtn}
            disabled={loading}
          >
            <Plus size={16} />
            Connect Meta Account
          </button>

          <button
            onClick={() => navigate('/camp')}
            className={styles.connectBtn}
          >
            Go to Dashboard
          </button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className={styles.alert}>
          <AlertCircle size={16} />
          <span>{error}</span>
          <button
            onClick={() => setError('')}
            className={styles.closeAlert}
            aria-label="Close alert"
          >
            ×
          </button>
        </div>
      )}

      {success && (
        <div className={styles.successAlert}>
          <CheckCircle size={16} />
          <span>{success}</span>
          <button
            onClick={() => setSuccess('')}
            className={styles.closeAlert}
            aria-label="Close alert"
          >
            ×
          </button>
        </div>
      )}

      {loading && (
        <div className={styles.loadingAlert}>
          <div className={styles.spinner}></div>
          <span>Loading your Meta connections...</span>
        </div>
      )}

      {/* Connection Stats */}
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <Users size={20} />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statValue}>{accounts.pages.length}</div>
            <div className={styles.statLabel}>Connected Pages</div>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <Building2 size={20} />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statValue}>{accounts.adAccounts.length}</div>
            <div className={styles.statLabel}>Connected Ad Accounts</div>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <CheckCircle size={20} />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statValue}>
              {accounts.pages.filter(p => p.webhook_subscribed).length}
            </div>
            <div className={styles.statLabel}>Active Webhooks</div>
          </div>
        </div>
      </div>

      {/* Facebook Pages Section */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionTitle}>
            <Users size={20} />
            <h2>Facebook Pages</h2>
            <span className={styles.count}>({accounts.pages.length})</span>
          </div>
          <div className={styles.sectionInfo}>
            <Info size={14} />
            <span>Pages are used to collect leads from your Facebook lead ads</span>
          </div>
        </div>

        {accounts.pages.length === 0 ? (
          <div className={styles.emptyState}>
            <Users size={48} />
            <h3>No pages connected</h3>
            <p>Connect your Facebook pages to start collecting leads from your lead generation campaigns.</p>
            <button onClick={handleConnect} className={styles.connectEmptyBtn}>
              <Facebook size={16} />
              Connect Facebook Page
            </button>
          </div>
        ) : (
          <div className={styles.tableWrapper}>
            <table className={styles.accountsTable}>
              <thead>
                <tr>
                  <th>Page Name</th>
                  <th>Page ID</th>
                  <th>Webhook Status</th>
                  <th>Connected</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {accounts.pages.map(page => (
                  <tr key={page.page_id}>
                    <td>
                      <div className={styles.accountName}>
                        <div className={styles.accountAvatar}>
                          <Users size={16} />
                        </div>
                        <div>
                          <div className={styles.name}>{page.page_name}</div>
                          <div className={styles.category}>{page.category || 'Facebook Page'}</div>
                        </div>
                      </div>
                    </td>
                    <td className={styles.idCell}>
                      <code>{page.page_id}</code>
                    </td>
                    <td>
                      <span className={`${styles.statusBadge} ${page.webhook_subscribed ? styles.active : styles.inactive}`}>
                        {page.webhook_subscribed ? (
                          <>
                            <CheckCircle size={12} />
                            Active
                          </>
                        ) : (
                          <>
                            <AlertCircle size={12} />
                            Inactive
                          </>
                        )}
                      </span>
                    </td>
                    <td className={styles.dateCell}>
                      {page.connected_at ? new Date(page.connected_at).toLocaleDateString() : 'Unknown'}
                    </td>
                    <td>
                      <div className={styles.actionButtons}>
                        <button
                          className={styles.actionBtn}
                          title="View on Facebook"
                          onClick={() => window.open(`https://facebook.com/${page.page_id}`, '_blank')}
                        >
                          <ExternalLink size={14} />
                        </button>
                        <button
                          onClick={() => handleDisconnectPage(page.page_id, page.page_name)}
                          className={`${styles.actionBtn} ${styles.dangerBtn}`}
                          disabled={disconnecting === page.page_id}
                          title="Disconnect page"
                        >
                          {disconnecting === page.page_id ? (
                            <div className={styles.miniSpinner}></div>
                          ) : (
                            <Trash2 size={14} />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Ad Accounts Section */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionTitle}>
            <Building2 size={20} />
            <h2>Ad Accounts</h2>
            <span className={styles.count}>({accounts.adAccounts.length})</span>
          </div>
          <div className={styles.sectionInfo}>
            <Info size={14} />
            <span>Ad accounts provide campaign data and performance metrics</span>
          </div>
        </div>

        {accounts.adAccounts.length === 0 ? (
          <div className={styles.emptyState}>
            <Building2 size={48} />
            <h3>No ad accounts connected</h3>
            <p>Connect your Facebook ad accounts to monitor campaign performance and track advertising spend.</p>
            <button onClick={handleConnect} className={styles.connectEmptyBtn}>
              <Facebook size={16} />
              Connect Ad Account
            </button>
          </div>
        ) : (
          <div className={styles.tableWrapper}>
            <table className={styles.accountsTable}>
              <thead>
                <tr>
                  <th>Account Name</th>
                  <th>Account ID</th>
                  <th>Status</th>
                  <th>Currency</th>
                  <th>Connected</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {accounts.adAccounts.map(acc => (
                  <tr key={acc.ad_account_id}>
                    <td>
                      <div className={styles.accountName}>
                        <div className={styles.accountAvatar}>
                          <Building2 size={16} />
                        </div>
                        <div>
                          <div className={styles.name}>{acc.ad_account_name}</div>
                          <div className={styles.category}>Ad Account</div>
                        </div>
                      </div>
                    </td>
                    <td className={styles.idCell}>
                      <code>{acc.ad_account_id}</code>
                    </td>
                    <td>
                      <span className={`${styles.statusBadge} ${acc.account_status === 'ACTIVE' ? styles.active : styles.inactive}`}>
                        {acc.account_status === 'ACTIVE' ? (
                          <>
                            <CheckCircle size={12} />
                            Active
                          </>
                        ) : (
                          <>
                            <AlertCircle size={12} />
                            {acc.account_status || 'Unknown'}
                          </>
                        )}
                      </span>
                    </td>
                    <td className={styles.currencyCell}>
                      {acc.currency || 'USD'}
                    </td>
                    <td className={styles.dateCell}>
                      {acc.connected_at ? new Date(acc.connected_at).toLocaleDateString() : 'Unknown'}
                    </td>
                    <td>
                      <div className={styles.actionButtons}>
                        <button
                          className={styles.actionBtn}
                          title="Open in Ads Manager"
                          onClick={() => window.open(`https://www.facebook.com/adsmanager/manage/accounts?act=${acc.ad_account_id.replace('act_', '')}`, '_blank')}
                        >
                          <ExternalLink size={14} />
                        </button>
                        <button
                          onClick={() => handleDisconnectAd(acc.ad_account_id, acc.ad_account_name)}
                          className={`${styles.actionBtn} ${styles.dangerBtn}`}
                          disabled={disconnecting === acc.ad_account_id}
                          title="Disconnect account"
                        >
                          {disconnecting === acc.ad_account_id ? (
                            <div className={styles.miniSpinner}></div>
                          ) : (
                            <Trash2 size={14} />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Help Section */}
      <div className={styles.helpSection}>
        <div className={styles.helpCard}>
          <Settings size={24} />
          <div className={styles.helpContent}>
            <h3>Need Help?</h3>
            <p>If you're having trouble connecting your Meta accounts, make sure you have admin access to the Facebook pages and ad accounts you want to connect.</p>
            <ul>
              <li>You must be an admin of the Facebook page</li>
              <li>You need admin access to the ad account</li>
              <li>Your Meta app must be approved for production use</li>
              <li>All required permissions must be granted</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConnectMetaAds;
